import { useState } from 'react';
import { getApiErrorMessage } from '@/api/errors';
import useProductListMutations from '@/api/mutations/useProductListMutations';
import useGetProductListQuery from '@/api/queries/useGetProductListQuery';
import useGetProductListRetailerAssignmentsQuery from '@/api/queries/useGetProductListRetailerAssignmentsQuery';
import CustomToast from '@/components/CustomToast';
import PageHeader from '@/components/PageHeader';
import { RetailerAdditiveComboBoxItem } from '@/components/RetailerAdditiveComboBox';
import useSearch from '@/components/search/useSearch';
import useStagedSelection from '@/hooks/useStagedSelection';
import { Retailer } from '@/models/Retailer';
import Breadcrumbs from '@/modules/product/components/Breadcrumbs';
import ProductListDetailHeader from '@/modules/product/components/ProductListDetailHeader';
import ProductListLoadingState from '@/modules/product/components/ProductListLoadingState';
import ProductListRetailerAssignmentForm from '@/modules/product/components/ProductListRetailerAssignmentForm';
import ProductListRetailerTable from '@/modules/product/components/ProductListRetailerTable';
import ProductListUnavailableState from '@/modules/product/components/ProductListUnavailableState';
import { InformationCircleIcon } from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';
import { useParams } from 'react-router-dom';

const ProductListRetailerList = () => {
  const { productListId } = useParams();
  const { page, handlePageChange } = useSearch();
  const { productList, isLoading, error } = useGetProductListQuery(productListId);
  const retailerAssignmentsQuery = useGetProductListRetailerAssignmentsQuery(productListId, page);
  const { assignRetailersToProductList, unassignRetailersFromProductList } =
    useProductListMutations();
  const staged = useStagedSelection<Retailer>();
  const [submitting, setSubmitting] = useState(false);

  if (isLoading) {
    return <ProductListLoadingState label="Loading Product List" />;
  }

  if (!productListId || !productList || error) {
    return <ProductListUnavailableState />;
  }

  const handleSelect = (item: RetailerAdditiveComboBoxItem) => {
    staged.addItem(item.retailer);
  };

  const handleAssign = async () => {
    if (staged.totalCount === 0) return;

    setSubmitting(true);
    try {
      await assignRetailersToProductList({
        productListId,
        ids: staged.stagedIds.map(String),
      });
      staged.clear();
      toast.custom((t) => (
        <CustomToast
          id={t.id}
          message="Selected retailers assigned to the Product List."
          visible={t.visible}
        />
      ));
    } catch (caught) {
      const message = await getApiErrorMessage(
        caught,
        'Failed to assign the selected retailers. Please try again.',
      );
      toast.custom((t) => (
        <CustomToast id={t.id} message={message} visible={t.visible} variant="error" />
      ));
    } finally {
      setSubmitting(false);
    }
  };

  const handleUnassign = async (retailerId: string) => {
    try {
      await unassignRetailersFromProductList({ productListId, ids: [retailerId] });
      toast.custom((t) => (
        <CustomToast
          id={t.id}
          message="Retailer removed from the Product List."
          visible={t.visible}
        />
      ));
    } catch (caught) {
      const message = await getApiErrorMessage(
        caught,
        'Failed to remove the retailer. Please try again.',
      );
      toast.custom((t) => (
        <CustomToast id={t.id} message={message} visible={t.visible} variant="error" />
      ));
      throw caught;
    }
  };

  return (
    <div>
      <Breadcrumbs />
      <ProductListDetailHeader productList={productList} />
      <PageHeader title="Retailer Assignments" />

      <div
        role="note"
        className="mb-4 flex items-start gap-2 rounded-md bg-blue-50 p-4 text-sm text-blue-800"
      >
        <InformationCircleIcon className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
        <p>
          Assigning a list to a retailer makes it available to all of that retailer’s connected
          stores. If you assign multiple active lists, products from all those lists are included.
          Stores with their own product lists use those instead.
        </p>
      </div>

      <ProductListRetailerAssignmentForm
        productListId={productListId}
        supplierId={productList.supplierId}
        selectedIds={staged.stagedIds}
        stagedCount={staged.totalCount}
        onSelect={handleSelect}
        onAssign={handleAssign}
        submitting={submitting}
      />

      <ProductListRetailerTable
        assignmentQuery={retailerAssignmentsQuery}
        supplierId={productList.supplierId}
        stagedItems={staged.stagedItems}
        onUnstage={staged.removeItem}
        page={page}
        onChangePage={handlePageChange}
        onUnassign={handleUnassign}
      />
    </div>
  );
};

export default ProductListRetailerList;
